import { format, isValid, parse } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { dateToDateValue, dateValueToDate } from "@smartsuite/fields-logic/lib/utils/date";

import { DateFieldValue } from "./date.config";

// A value may arrive as a Date (prefill/default) or a stored ISO string (control). fields-logic
// parses ISO strings, so normalize to ISO first. An unparseable Date has no ISO form — toISOString
// throws on it — so it is reported as unusable instead.
const toIso = (date: string | Date): string | null => {
  if (date instanceof Date) {
    return isValid(date) ? date.toISOString() : null;
  }

  return date;
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// new Date("nonsense") yields an Invalid Date *object*, not null, and every consumer downstream
// (react-datepicker's own date-fns format calls, formatInTimeZone) throws RangeError on it
// (FORMS-WEB-X5). Parse through this so an unusable value degrades to an empty field instead.
// A field default arrives as a bare "yyyy-MM-dd" string, which new Date() reads as UTC midnight —
// the previous day for any viewer behind UTC (BUG-21401). Parse that shape as a local calendar day.
const toValidDate = (value: string | Date): Date | null => {
  if (typeof value === "string" && DATE_ONLY_PATTERN.test(value)) {
    const day = parse(value, "yyyy-MM-dd", new Date());

    return isValid(day) ? day : null;
  }

  const parsed = value instanceof Date ? value : new Date(value);

  return isValid(parsed) ? parsed : null;
};

// The picker (react-datepicker) always renders a Date in the browser's local time. A value with a
// time must therefore be shown in the profile timezone so the picker's local wall-clock equals the
// profile wall-clock (the user's intent). A date-only value carries no time to shift, so it is kept
// on the existing browser-local path untouched.
export const dateValueToPickerDate = (value: DateFieldValue, timezone: string): Date | null => {
  if (!value?.date) return null;

  if (!value.include_time) {
    return toValidDate(value.date);
  }

  const iso = toIso(value.date);
  if (iso === null) return null;

  const picked = dateValueToDate({ date: iso, include_time: true }, timezone);

  return picked && isValid(picked) ? picked : null;
};

// Inverse of dateValueToPickerDate for storage. A with-time pick is the profile wall-clock, stored
// as the matching UTC instant (ISO). A date-only pick keeps the existing browser-local toString
// shape so nothing about the date-only flow changes.
export const pickerDateToDateValue = (
  date: Date | null,
  includeTime: boolean,
  timezone: string
): DateFieldValue => {
  if (!date) return { date: null, include_time: false };

  if (!includeTime) {
    return { date: date.toString(), include_time: false };
  }

  return { date: dateToDateValue(date, true, timezone).date, include_time: true };
};

// The displayed value mirrors the field controls' behaviour: a value with a time is rendered in the
// profile timezone with the "(GMT±HH:MM)" offset (OOOO); a date-only value shows just the day.
export const formatDateFieldValue = (
  value: DateFieldValue,
  timeFormat: "12h" | "24h",
  timezone: string
): string => {
  if (!value?.date) return "";

  const parsed = toValidDate(value.date);
  if (!parsed) return "";

  const timeFormatPart = timeFormat === "12h" ? "hh:mm a" : "HH:mm";

  return value.include_time
    ? formatInTimeZone(parsed, timezone, `MMMM dd, yyyy, ${timeFormatPart} (OOOO)`)
    : format(parsed, "MMMM dd, yyyy");
};
